/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useContext, useState, useMemo } from 'react';
import _, { get } from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from '@xstate/react';

import { FormErrorGroup, UnmappedFormErrors } from '@bingads-webui-campaign-react/form-error';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { withPerformanceMarker } from '@bingads-webui-campaign-react/performance-marker-provider';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { AdPreview, DisplayAdsContainer, PopulateImages, imagePlacementsToImage } from '@bingads-webui-campaign-react/xandr-display-ads-campaign-shared-components';
import { useImagePicker } from '@bingads-webui-campaign-react/add-image-hooks';

import { useStepAdsSettingsState } from './use-step-ads-state';

const permissionsSelector = state => get(state, 'context.stepsData.permissions');

export const getODataConfig = (serverContext) => {
  const isLocal = () => {
    const hostname = document.location.hostname.toLowerCase();
    return hostname.indexOf('ads.microsoft.com') === -1 &&
        hostname.indexOf('ads-int.microsoft.com') === -1;
  };

  const odataPath = isLocal() ? 'Advertiser/V2' : 'Campaign/V2';

  const oDataConfig = {
    odataToken: serverContext.Token,
    oDataToken: serverContext.Token,
    odataPath: `${serverContext.ServicesEndpoints.CampaignAPI}${odataPath}`,
    oDataPath: `${serverContext.ServicesEndpoints.CampaignAPI}${odataPath}`,
  };

  return oDataConfig;
};

const StepAdsSettingsInternal = (props) => {
  const {
    stepService,
    perfMarker,
    scope,
  } = props;

  const { aid, cid, campaignId } = scope.values;
  const activity = useContext(ActivityContext);
  const [stepState, stepApi] = useStepAdsSettingsState(stepService, activity);
  const permissions = useSelector(stepService, permissionsSelector);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const { onAdsValidation } = stepApi;
  const {
    i18n,
    hideStepHeader,
    ads,
    adsErrors, otherErrors,
    navigationConfirmationPrompt,
    systemErrors,
    areBeaconsActive,
    serverErrors,
    showAllValidationErrors,
    serverContext,
  } = stepState;
  const oDataConfig = useMemo(() => getODataConfig(serverContext), [serverContext]);

  const {
    displayImagePicker,
    setDisplayImagePicker,
    selectedMediaPanelSource,
    setSelectedMediaPanelSource,
  } = useImagePicker();

  const onNewImageSelected = useCallback((imagePlacements) => {
    const newAds = imagePlacements.map(imagePlacement => (imagePlacementsToImage({
      crops: imagePlacement,
      defaults: {
        FinalUrls: [],
        isValid: false,
        isDirty: false,
      },
    })));
    const result = {
      data: [...newAds],
      isValid: false,
      isDirty: true,
    };
    onAdsValidation(result);
    setDisplayImagePicker(false);
  }, [onAdsValidation, setDisplayImagePicker]);

  const getBasicSettingsSection = useCallback(() => (
    <div className={classNames('basic-ads-section')}>

      <div style={{ display: 'flex', columnGap: '50px' }}>
        <div style={{ width: '500px' }}>
          {i18n.getString(_TL_('Now that you\'ve created your ad group, you can create your ads. Add your images and the Final URL, and we\'ll show you a preview of how your ads might look like.'))}
          {_.isEmpty(ads.data) &&
          <PopulateImages
            aid={aid}
            campaignId={campaignId}
            i18n={i18n}
            perfMarker={perfMarker}
            permissions={permissions}
            oDataConfig={oDataConfig}
            serverContext={serverContext}
            onNewImageSelected={onNewImageSelected}
            selectedMediaPanelSource={selectedMediaPanelSource}
            setSelectedMediaPanelSource={setSelectedMediaPanelSource}
            displayImagePicker={displayImagePicker}
            setDisplayImagePicker={setDisplayImagePicker}
          />
          }
          { !_.isEmpty(ads.data) &&
          <DisplayAdsContainer
            aid={aid}
            cid={cid}
            campaignId={campaignId}
            i18n={i18n}
            perfMarker={perfMarker}
            onAdsValidation={onAdsValidation}
            currentImageIndex={currentImageIndex}
            ads={ads.data}
            oDataConfig={oDataConfig}
            permissions={permissions}
          />
          }
        </div>
        {
          !_.isEmpty(ads.data) &&
          <div style={{ width: 600, minHeight: 650 }}>
            <AdPreview
              ads={ads}
              i18n={i18n}
              perfMarker={perfMarker}
              onUpdateCurrentImageIndex={setCurrentImageIndex}
            />
          </div>
        }
      </div>
    </div>), [i18n, stepService, perfMarker, cid, aid, ads, currentImageIndex, oDataConfig, permissions, displayImagePicker, selectedMediaPanelSource, setSelectedMediaPanelSource, onNewImageSelected, serverContext]);

  return (
    <>
      <ActuationGroup actuationProps={{ isActive: areBeaconsActive }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
            {!hideStepHeader &&
            <>
              <h1>{i18n.getString(_TL_('Ads'))}</h1>
              <span>{i18n.getString(_TL_(''))}</span>
            </>
            }
            <div className="ads-server-errors">
              <ErrorMessages messages={adsErrors} />
            </div>
            <div className="other-server-errors">
              <UnmappedFormErrors />
            </div>
            <div className="other-server-errors">
              <ErrorMessages messages={otherErrors} />
            </div>
            <div className="system-errors">
              <ErrorMessages messages={systemErrors} />
            </div>
            {getBasicSettingsSection()}
            {navigationConfirmationPrompt}
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </>
  );
};

StepAdsSettingsInternal.propTypes = {
  stepService: PropTypes.shape({}).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  instrumentation: PropTypes.shape({}).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      aid: PropTypes.number,
      cid: PropTypes.number,
      campaignId: PropTypes.number,
    }),
  }).isRequired,
};

export const StepAdsSettings = withPerformanceMarker(StepAdsSettingsInternal);
